import React from 'react';
import { LucideIcon } from 'lucide-react';

interface StatefulIconProps {
  icon: LucideIcon;
  active?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

/**
 * StatefulIcon - Wrapper component for icons with active/inactive states
 * 
 * Provides consistent visual distinction between active and inactive icons:
 * - Inactive: Outline only (stroke-2)
 * - Active: Filled appearance (fill-current, stroke-1)
 * 
 * @param icon - Lucide React icon component
 * @param active - Whether the icon is in active state (default: false)
 * @param size - Icon size preset (default: 'md')
 * @param className - Additional CSS classes
 * 
 * @example
 * // Navigation item
 * <StatefulIcon 
 *   icon={Home} 
 *   active={isActive}
 *   size="md"
 *   className="text-white"
 * />
 * 
 * @example
 * // View toggle
 * <StatefulIcon 
 *   icon={Grid3X3} 
 *   active={viewMode === 'grid'}
 *   size="sm"
 * />
 * 
 * @example
 * // Bookmark button
 * <StatefulIcon 
 *   icon={Bookmark} 
 *   active={isBookmarked}
 *   className={isBookmarked ? 'text-primary-600' : 'text-text-tertiary'}
 * />
 */
const StatefulIcon: React.FC<StatefulIconProps> = ({ 
  icon: Icon, 
  active = false, 
  size = 'md',
  className = ''
}) => {
  // Size presets following design system standards
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-8 h-8'
  };
  
  // State-based styling
  // Active: filled appearance with thinner stroke
  // Inactive: outline only with standard stroke
  const stateClasses = active 
    ? 'fill-current stroke-[1.5]' 
    : 'fill-none stroke-2';
  
  return (
    <Icon 
      className={`${sizeClasses[size]} ${stateClasses} ${className}`}
      aria-hidden="true"
    />
  );
};

export default StatefulIcon;








